import React from 'react';

import Card from '../../components/Card/Card';
import ChartWrapper from '../../components/ChartWrapper/ChartWrapper';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import BarChart from '../../components/BarChart/BarChart';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import ChartTooltip from '../../components/ChartTooltip/ChartTooltip';
import styles from './CalendarYearTotalReturnsCard.css';
import { displayPercentage } from '../../utils/Format/Format';
import { showTackedFootnotes } from '../../utils/fund-info';
import { CARD_NAMES } from '../../utils/constants';
import { isExpectingFullYearData } from '../../utils/time';

import { calendarYearTotalReturnsCardUtil } from './util';
import { CALENDAR_YEAR_TOTAL_RETURNS } from './queries';

const CalendarYearTotalReturnsCard = ({ ticker, iClass, moneyMarket }) => (
    <Card>
        <GqlQueryWrapper
            query={CALENDAR_YEAR_TOTAL_RETURNS}
            variables={{ ticker }}
            dataFunc={calendarYearTotalReturnsCardUtil}
            placeholder={() => <TablePreRender />}
        >
            {({ data: { chartData, inceptionDate, hasTacked } }) => {
                if (!(chartData && chartData.length && chartData[9])) {
                    if (isExpectingFullYearData(inceptionDate)) {
                        return (
                            <ErrorMessageNoData
                                cardName={CARD_NAMES.CALENDAR_YEAR_CHART}
                            />
                        );
                    }
                    return (
                        <ErrorMessageNewFund
                            cardName={CARD_NAMES.CALENDAR_YEAR_CHART}
                        />
                    );
                }

                return (
                    <React.Fragment>
                        <TitleWithDescription
                            title={
                                <React.Fragment>
                                    Calendar Year Total Returns
                                    {hasTacked &&
                                        showTackedFootnotes(iClass, ticker) && (
                                            <sup>1</sup>
                                        )}
                                </React.Fragment>
                            }
                        />
                        <ChartWrapper
                            tipContent={(d) => {
                                const formattedData = {
                                    ...d,
                                    label: `${d.label} Total Returns`
                                };
                                return (
                                    <ChartTooltip
                                        data={formattedData}
                                        format={(val) => displayPercentage(val)}
                                    />
                                );
                            }}
                        >
                            {({
                                marginLeft,
                                width,
                                toggleTooltip,
                                updateTooltip
                            }) => (
                                <BarChart
                                    data={chartData}
                                    width={width}
                                    height={300}
                                    marginLeft={marginLeft}
                                    toggleTooltip={toggleTooltip}
                                    updateTooltip={updateTooltip}
                                    formatYAxis=".0%"
                                    formatYAxisDec=".2%"
                                    formatXAxis={[
                                        [/((^\d{2})(\d{2}$))/g, `$3`]
                                    ]}
                                />
                            )}
                        </ChartWrapper>
                        {moneyMarket && (
                            <div className="disclaimer">
                                Average annual total return figures include
                                reinvested dividends and capital gain
                                distributions, if any.
                            </div>
                        )}
                        {hasTacked &&
                            showTackedFootnotes(iClass, ticker) && (
                                <div className="disclaimer">
                                    <sup>1</sup> The T. Rowe Price Funds - I
                                    Class share the portfolio of an existing
                                    fund (the original share class of the fund
                                    is referred to as the “investor class”). The
                                    total return figures for I Class shares have
                                    been calculated using the performance data
                                    of the investor class up to the inception
                                    date of the I Class (shown in the investment
                                    objective, strategy, and risks section) and
                                    the actual performance results of the I
                                    Class since that date. Because the I Classes
                                    are expected to have lower expenses than the
                                    Investor Classes, the I Class performance,
                                    had it existed over the periods shown, would
                                    have been higher. The inception date shown
                                    in the returns table above is the date on
                                    which the investor class started operations
                                </div>
                            )}
                    </React.Fragment>
                );
            }}
        </GqlQueryWrapper>
        <style jsx>{styles}</style>
    </Card>
);

export default CalendarYearTotalReturnsCard;
